import type { Shortcut, ShortcutGroup } from '../lib/keyboard.js';
import { Dialog } from './ui/Dialog.js';

interface Props {
  open: boolean;
  onClose: () => void;
  /** R12 — the SAME registry the hook binds, so the help can't advertise a dead key. */
  shortcuts: Shortcut[];
}

const GROUP_ORDER: ShortcutGroup[] = ['Navigate', 'Triage', 'Help'];

/**
 * The `?` keyboard-shortcut overlay. Renders directly from the bound registry
 * (R12) — every row is a live binding, so an advertised-but-dead shortcut is
 * structurally impossible. Mounts through the shared Dialog primitive (R3): one
 * scrim/z-scale, role=dialog + aria-modal, Esc + scrim close, focus trap.
 */
export function ShortcutHelp({ open, onClose, shortcuts }: Props) {
  // R12 review — render registry-COMPLETE: order by GROUP_ORDER but append any
  // group present in the registry that GROUP_ORDER forgot, so a bound shortcut
  // can never be silently hidden (the exact advertised-vs-bound gap R12 closes).
  const present = [...new Set(shortcuts.map((s) => s.group))];
  const ordered = [
    ...GROUP_ORDER.filter((g) => present.includes(g)),
    ...present.filter((g) => !GROUP_ORDER.includes(g)),
  ];
  const byGroup = ordered.map((group) => ({
    group,
    items: shortcuts.filter((s) => s.group === group),
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy="shortcut-help-title"
      testId="shortcut-help"
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-5 text-zinc-100 shadow-xl"
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 id="shortcut-help-title" className="text-sm font-semibold">
          Keyboard shortcuts
        </h2>
        <button
          type="button"
          onClick={onClose}
          data-testid="shortcut-help-close"
          className="text-[11px] text-zinc-400 hover:text-zinc-200"
          aria-label="Close shortcut help"
        >
          Close · Esc
        </button>
      </header>
      <p className="mb-3 text-[11px] text-zinc-500">
        Shortcuts apply outside text inputs. Modifier keys (⌘ / Ctrl / Alt) are reserved for the
        browser. Press <kbd className="font-mono">g</kbd> then a key to jump.
      </p>
      <div className="space-y-3">
        {byGroup.map(({ group, items }) => (
          <section key={group}>
            <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              {group}
            </h3>
            <ul className="space-y-1.5 text-[12px]">
              {items.map((s) => (
                <li
                  key={s.id}
                  data-testid={`shortcut-${s.id}`}
                  className="grid grid-cols-[7rem_1fr] items-center gap-3"
                >
                  <span className="flex flex-wrap items-center gap-1">
                    {s.keys.map((k) => (
                      <kbd
                        key={`${s.id}-${k}`}
                        className="inline-flex min-w-5 items-center justify-center rounded border border-zinc-700 bg-zinc-950 px-1.5 py-0.5 font-mono text-[10px] text-zinc-200"
                      >
                        {k}
                      </kbd>
                    ))}
                  </span>
                  <span className="text-zinc-300">{s.label}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <footer className="mt-4 text-[10px] text-zinc-500">
        No shortcut in this build replies, dispatches, or triggers a destructive action.
      </footer>
    </Dialog>
  );
}
